import { Combobox } from '@headlessui/react'
import { clsx } from 'clsx'
import { Link } from 'react-router-dom'

import { AskAiAvailability } from '@tribeplatform/gql-client/types'
import { useAskAIAccess, useAuthMember } from '@tribeplatform/react-sdk/hooks'
import { Badge } from '@tribeplatform/react-ui-kit/Badge'
import { IconButton } from '@tribeplatform/react-ui-kit/Button'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'

import { T, useI18n } from '../../../i18n/index.js'
import { ResultsView } from '../../SmartSearch.types.js'
import { AiStarIcon } from '../AiStarIcon.js'

type Props = {
  text: string
  className?: string
  onSubmit?: () => void
  view?: ResultsView
  isHidden?: boolean
}

export const AskAIResultRow = ({
  text,
  className,
  onSubmit,
  view = 'command-bar',
  isHidden = false,
}: Props) => {
  const { isAdmin } = useAuthMember()

  const { $t } = useI18n()
  const label = text
    ? `${$t({
        id: 'SmartSearch.AskAI.AskAIPrefix',
        defaultMessage: 'Ask AI',
      })} `
    : $t({
        id: 'SmartSearch.AskAI.AskAIAbout',
        defaultMessage: 'Ask AI about...',
      })

  const quotedText = `"${text}"`

  const { canAskAi, askAiAvailability } = useAskAIAccess()

  if (!canAskAi) {
    return null
  }

  return (
    <div className={clsx(className, { hidden: isHidden }, 'px-2 py-2')}>
      <Combobox.Option
        disabled={isHidden}
        value={{ action: onSubmit, preventClose: true }}
        className={({ active }) =>
          clsx(
            'px-3 flex cursor-pointer select-none items-center rounded-md',
            active && 'bg-surface-subdued',
          )
        }
      >
        {({ active }) => (
          <>
            <div
              className="flex w-full justify-between py-3 items-center group"
              data-active={active}
            >
              <div className="flex gap-2 flex-1 items-center overflow-hidden">
                <AiStarIcon />

                <div className="flex flex-1 gap-1 whitespace-nowrap w-full overflow-hidden">
                  <span>{label}</span>
                  <span
                    className={clsx(
                      'flex-1 pr-1 truncate',
                      text ? 'flex' : 'hidden',
                    )}
                  >
                    {quotedText}
                  </span>
                </div>

                {askAiAvailability === AskAiAvailability.staffOnly && (
                  <div className="flex gap-1 items-center">
                    <Badge
                      tooltip={
                        <div className="max-w-60">
                          <T
                            id="SmartSearch.AskAI.OnlyVisibleToAdminsAndModsTooltip"
                            defaultMessage="Only available to staff. Click the settings icon to manage access for your community members."
                          />
                        </div>
                      }
                      tooltipProps={{
                        offsetValue: 12,
                      }}
                      leadingIcon={<SvgIcon name="eye-off" />}
                      className={clsx(
                        'hidden md:flex items-center gap-1 rounded-full text-label-sm whitespace-nowrap group-hover:bg-surface cursor-default',
                        {
                          'py-0.5 px-2 md:text-label-sm':
                            view === 'command-bar',
                          'py-1 px-3 md:text-label-md': view === 'search-page',
                        },
                      )}
                      onClick={e => e.stopPropagation()}
                    >
                      <T
                        id="SmartSearch.AskAI.OnlyVisibleToAdminsAndMods"
                        defaultMessage="Only available to staff"
                      />
                    </Badge>

                    {isAdmin && (
                      <IconButton
                        tabIndex={-1}
                        as={Link}
                        to="/manage/settings/search"
                        rounded
                        tooltip
                        accessibilityLabel={$t({
                          id: 'Generics.Settings',
                          defaultMessage: 'Settings',
                        })}
                        tooltipProps={{
                          offsetValue: 12,
                        }}
                        size={view === 'command-bar' ? 'sm' : 'md'}
                        variant="plainNeutral"
                        className="group/icon-button"
                        icon={
                          <SvgIcon
                            name="sliders"
                            size={view === 'command-bar' ? 'md' : 'lg'}
                            className="text-content-subdued group-hover/icon-button:text-content"
                          />
                        }
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </Combobox.Option>
    </div>
  )
}
